import type { ShortcutContext } from 'src/stores/shortcuts/types';

/**
 * DOM-freies Abbild eines registrierten Shortcut-Scopes für die pure Dispatch-Auflösung.
 * Sichtbarkeit, Fokus und Modal-Zugehörigkeit werden vom Aufrufer aus dem DOM abgeleitet.
 */
export interface ResolvableScope {
	containsFocus: boolean;
	context: ShortcutContext;
	handledActionIds: ReadonlySet<string>;
	insideOpenModal: boolean;
	lastInteractionOrder: number;
	registrationOrder: number;
	visible: boolean;
}

/**
 * Leitet die aktiven Shortcut-Kontexte ausschließlich aus sichtbaren Scopes ab.
 * `global` ist immer aktiv. Hintergrund-Tabs (unsichtbar) tragen keinen Kontext bei (E2/E3).
 */
export function resolveActiveContexts(scopes: ReadonlyArray<ResolvableScope>): ShortcutContext[] {
	const contexts: ShortcutContext[] = ['global'];

	for (const scope of scopes) {
		if (scope.visible && !contexts.includes(scope.context)) {
			contexts.push(scope.context);
		}
	}

	return contexts;
}

function compareDispatchPriority(left: ResolvableScope, right: ResolvableScope): number {
	if (left.containsFocus !== right.containsFocus) {
		return left.containsFocus ? -1 : 1;
	}

	if (left.lastInteractionOrder !== right.lastInteractionOrder) {
		return right.lastInteractionOrder - left.lastInteractionOrder;
	}

	return right.registrationOrder - left.registrationOrder;
}

/**
 * Wählt den einen Ziel-Scope für eine Action (Dispatch-Invariante):
 * sichtbar, nicht modal-verdeckt, dann Fokus > letzte Interaktion > jüngste Registrierung.
 * Liefert `null`, wenn kein Scope die Action ausführen darf — es feuert nie mehr als ein Handler.
 */
export function resolveDispatchScope(
	scopes: ReadonlyArray<ResolvableScope>,
	actionId: string,
	modalOpen: boolean
): ResolvableScope | null {
	const candidates = scopes.filter(
		(scope) => scope.visible && scope.handledActionIds.has(actionId) && (!modalOpen || scope.insideOpenModal)
	);

	if (candidates.length === 0) {
		return null;
	}

	return [...candidates].sort(compareDispatchPriority)[0] ?? null;
}

const EDITOR_BYPASS_MODIFIERS: ReadonlyArray<string> = ['Mod', 'Meta', 'Ctrl', 'Alt'];

/**
 * Editor-Gate (E6): Im Editor-Fokus feuern nur explizit freigegebene Actions
 * (`allowInEditor`) und nur als Einzel-Kombo mit echtem Modifier — Shift allein
 * tippt Zeichen, Chords würden Texteingabe schlucken.
 */
export function shouldBypassEditorGate(action: { allowInEditor?: boolean } | undefined, keys: string): boolean {
	if (action?.allowInEditor !== true) {
		return false;
	}

	if (keys.includes(' ')) {
		return false;
	}

	return keys.split('+').some((token) => EDITOR_BYPASS_MODIFIERS.includes(token));
}
